"use client"
import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { toast } from "sonner"
import { Loader2, RefreshCw, Sparkles, TrendingUp, Users, Target, Zap, ArchiveX, CheckCircle2, Pencil, ArchiveRestore, ChevronDown, Phone, Search, X, UserCheck, Filter, ChevronUp, Flame, Snowflake, Ban, ChevronLeft, ChevronRight, UserPlus, ListTodo, Bell } from "@/lib/icons"
import { useIsMobile } from "@/hooks/use-mobile"

import { ProtectedRoute } from "@/components/protected-route"
import { Sidebar } from "@/components/sidebar"
import { KanbanColumn } from "@/components/kanban/kanban-column"
import { LeadUnifiedView } from "@/components/kanban/lead-unified-view"
import { LeadStatusModal } from "@/components/kanban/lead-status-modal"
import { ArchivedLeadsModal } from "@/components/kanban/archived-leads-modal"
import { InscritosModal } from "@/components/kanban/inscritos-modal"
import { SinAtenderModal } from "@/components/kanban/sin-atender-modal"
import { LeadLevelLegend } from "@/components/kanban/lead-level-legend"
import { KanbanPageSkeleton } from "@/components/ui/skeleton"
import { useAuth } from "@/context/auth-context"

import { getLeads, updateLead, createLead, createLeadChild, supabase, getCurrentCampus, getAsesorIdByEmail, deleteLead } from "@/lib/supabase"
import { LeadEditorModal } from "@/components/kanban/lead-editor-modal"
import { KANBAN_STAGES, LEAD_STAGE_META, LEAD_STAGE_ORDER, STAGES, normalizeLeadStage, toDbStatus, fromDbStatus } from "@/lib/lead-stages"
import { LeadLevelAvatar } from "@/components/kanban/lead-level-avatar"
import { HeroBanner } from "@/components/kanban/hero-banner"
import { TasksPanel } from "@/components/kanban/tasks-panel"
import { TransferRequestModal } from "@/components/kanban/transfer-request-modal"
import { usePendingTransfers } from "@/hooks/usePendingTransfers"
import { getDaysSinceUpdate, getLastInteractionDate } from "@/lib/dates"

const KANBAN_COLUMNS = KANBAN_STAGES

const formatPercentage = (value) => (Number.isFinite(value) ? `${Math.round(value)}%` : "0%")
const parseLeadTags = (raw) => {
  if (!raw) return []

  if (Array.isArray(raw)) {
    return raw.map((tag) => String(tag).trim()).filter(Boolean)
  }

  const value = String(raw).trim()
  if (!value) return []

  if (value.startsWith("[") && value.endsWith("]")) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return parsed.map((tag) => String(tag).trim()).filter(Boolean)
      }
    } catch {
      // ignore JSON errors and fallback to comma split
    }
  }

  const tags = value
    .split(/[,\|]/)
    .map((tag) => tag.trim())
    .filter(Boolean)

  return Array.from(new Set(tags))
}

const ensureLeadTag = (currentTags, newTag) => {
  const tags = parseLeadTags(currentTags)
  const normalized = newTag.toLowerCase()
  if (!tags.some((tag) => tag.toLowerCase() === normalized)) {
    tags.push(newTag)
  }
  return tags
}

const appendLeadNote = (lead, addition) => {
  if (!addition) return lead?.notas || null
  const stamp = `[${new Date().toLocaleString()}] ${addition.trim()}`
  const existing = lead?.notas?.trim()
  return existing ? `${stamp}\n${existing}` : stamp
}

// Emails de admin supremo (pueden ver todos los campus)
const OWNER_EMAILS = new Set([
  "admin@tepeyac.mx",
  "ramiro@tepeyact.com",
])

export default function KanbanPage() {
  // campus efectivo: preferir la selección global (owner) guardada en localStorage
  // inicializamos con localStorage/getCurrentCampus y luego sincronizamos con user.campus si no es owner
  const [selectedCampus, setSelectedCampus] = useState(() => {
    try { return localStorage.getItem("selected_campus") || getCurrentCampus() || "Todos" } catch { return getCurrentCampus() || "Todos" }
  })

  useEffect(() => {
    const onCampus = (e) => {
      try {
        const c = e?.detail?.campus || localStorage.getItem("selected_campus")
        setSelectedCampus(c)
        // recargar leads al cambiar campus
        loadLeads({ showLoader: false })
      } catch { }
    }
    window.addEventListener("campusChanged", onCampus)
    return () => window.removeEventListener("campusChanged", onCampus)
  }, [])

  const [leads, setLeads] = useState([])
  const [draggedLead, setDraggedLead] = useState(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastSynced, setLastSynced] = useState(null)
  const [error, setError] = useState(null)
  const [detailLead, setDetailLead] = useState(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalData, setStatusModalData] = useState({ lead: null, targetStatus: null })
  const [archivedOpen, setArchivedOpen] = useState(false)
  const [headerExpanded, setHeaderExpanded] = useState(true) // 🆕 Estado para expandir/contraer
  const [highlightedLead, setHighlightedLead] = useState(null)
  const [inscritosOpen, setInscritosOpen] = useState(false) // Reemplaza showInscritoList
  const [sinAtenderOpen, setSinAtenderOpen] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [tasksPanelOpen, setTasksPanelOpen] = useState(false)
  const [pendingTasksCount, setPendingTasksCount] = useState(0)
  const [notificationsCount, setNotificationsCount] = useState(0)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchFocused, setSearchFocused] = useState(false)
  const [filterNivel, setFilterNivel] = useState(null) // null = todos
  const [filterCalidad, setFilterCalidad] = useState(null) // null = todos
  const [mobileActiveColumn, setMobileActiveColumn] = useState("nuevo")
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const isMobile = useIsMobile()
  const searchInputRef = useRef(null)
  const leadsRef = useRef([])
  const kanbanScrollRef = useRef(null)
  // Debounce para recargas de seguridad (ráfagas de realtime → un solo fetch)
  const reloadTimerRef = useRef(null)
  useEffect(() => {
    leadsRef.current = leads
  }, [leads])

  const { user } = useAuth()
  const userEmail = (user?.email || "").toLowerCase()
  // Considerar también los OWNER_EMAILS como admins supremos
  const isAdmin = Boolean(user?.isAdmin || user?.rol === "admin" || OWNER_EMAILS.has(userEmail))
  const isOwner = OWNER_EMAILS.has(userEmail)
  const [asesorId, setAsesorId] = useState(null)

  // Transferencias pendientes dirigidas a este asesor (TO) y enviadas por él (FROM)
  const { transfers: pendingTransfers, outgoing: outgoingTransfers, refresh: refreshTransfers } = usePendingTransfers(asesorId)
  const [activeTransfer, setActiveTransfer] = useState(null)
  const lastNotifiedRef = useRef(new Set())

  // Toast cuando llegue una nueva transferencia (no antes)
  useEffect(() => {
    if (!pendingTransfers?.length) return
    pendingTransfers.forEach((t) => {
      if (lastNotifiedRef.current.has(t.id)) return
      lastNotifiedRef.current.add(t.id)
      const fromName = t.from_asesor?.nombre || "Una asesora"
      const leadName = t.lead?.nombre_tutor || "un prospecto"
      toast(`${fromName} te quiere transferir a ${leadName}`, {
        description: "Click para revisar la solicitud",
        action: {
          label: "Ver",
          onClick: () => setActiveTransfer(t),
        },
        duration: 8000,
      })
    })
  }, [pendingTransfers])

  // Si el usuario no es owner y tiene campus asignado en su perfil, usarlo como seleccionado por defecto
  useEffect(() => {
    try {
      if (user && !isOwner) {
        const userCampus = user.campus || user.campus_id || null
        if (userCampus && (!selectedCampus || selectedCampus === "Todos")) {
          setSelectedCampus(userCampus)
        }
      }
    } catch (e) { /* ignore */ }
  }, [user, isOwner])

  useEffect(() => {
    let active = true
    if (!userEmail || isAdmin) {
      setAsesorId(null)
      return
    }

    getAsesorIdByEmail(userEmail)
      .then((id) => {
        if (active) setAsesorId(id || null)
      })
      .catch(() => {
        if (active) setAsesorId(null)
      })

    return () => {
      active = false
    }
  }, [userEmail])

  // Contador de tareas pendientes (para el badge en el botón "Tareas")
  useEffect(() => {
    if (!user) return
    let mounted = true

    const loadPendingCount = async () => {
      try {
        let query = supabase
          .from("tasks")
          .select("id", { count: "exact", head: true })
          .eq("completada", false)
        if (asesorId && !isAdmin) query = query.eq("asesor_id", asesorId)
        const { count } = await query
        if (mounted) setPendingTasksCount(count || 0)
      } catch (err) {
        console.warn("[KanbanPage] loadPendingCount:", err?.message)
      }
    }

    loadPendingCount()

    // Realtime: cualquier cambio en tasks recalcula el contador
    const channel = supabase
      .channel(`kanban-tasks-count-${userEmail || "anon"}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tasks" },
        () => { if (mounted) loadPendingCount() },
      )
      .subscribe()

    return () => {
      mounted = false
      supabase.removeChannel(channel)
    }
  }, [user, asesorId, isAdmin, userEmail])



  const sortLeads = useCallback((items) => {
    // Orden: leads RECIÉN CREADOS arriba (created_at DESC). Si created_at empata
    // o es null, usa updated_at como tiebreaker. La urgencia (3+ días sin
    // contestar) se sigue marcando visualmente en la tarjeta con anillo + badge.
    const createdOf = (lead) =>
      new Date(lead.created_at || lead.updated_at || 0).getTime()
    const updatedOf = (lead) =>
      new Date(lead.updated_at || lead.created_at || 0).getTime()

    return [...items].sort((a, b) => {
      const diff = createdOf(b) - createdOf(a)
      if (diff !== 0) return diff
      return updatedOf(b) - updatedOf(a)
    })
  }, [])

  const loadLeads = useCallback(
    async (opts = { showLoader: true }) => {
      if (!userEmail && !isAdmin) return

      if (opts.showLoader) {
        setLoading(true)
      } else {
        setRefreshing(true)
      }

      setError(null)

      const { data, error: leadsError } = await getLeads(isAdmin ? null : userEmail)

      if (leadsError) {
        console.error("[kanban] Error loading leads:", leadsError)
        setError("No pudimos cargar tus leads. Intenta nuevamente en unos segundos.")
      } else if (data) {
        // Si owner seleccionó un campus específico (no "Todos"), filtrar la lista
        let filtered = data
        try {
          const global = localStorage.getItem("selected_campus")

          // Helper: compara un valor contra todos los campos de campus del lead
          const matchesCampus = (lead, value) => {
            if (!value) return false
            const v = String(value).toLowerCase()
            return [lead.campus, lead.campus_id, lead.campus_codigo, lead.campus_nombre]
              .filter(Boolean)
              .some(field => String(field).toLowerCase() === v)
          }

          // Sólo OWNER (admin supremo) aplica el filtro global; admins normales y asesores no
          if (isOwner && global && global !== "Todos") {
            filtered = (data || []).filter(l => matchesCampus(l, global))
          } else if (!isOwner && !isAdmin && user?.campus) {
            // Solo asesores (no admins) filtran por su campus
            filtered = (data || []).filter(l => matchesCampus(l, user.campus) || matchesCampus(l, user.campus_id))
          }
        } catch { }
        setLeads(sortLeads(filtered))
        setLastSynced(new Date())
      }

      setLoading(false)
      setRefreshing(false)
    },
    [isAdmin, sortLeads, userEmail, user],
  )

  // 🆕 Recarga de seguridad debounced: varias ráfagas de realtime colapsan en
  // un solo loadLeads. Se usa como red cuando un evento (ej. INSERT de lead) se
  // pierde bajo carga pero sí llega el de mensaje.
  const scheduleReload = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current)
    reloadTimerRef.current = setTimeout(() => {
      reloadTimerRef.current = null
      loadLeads({ showLoader: false })
    }, 500)
  }, [loadLeads])

  // 🆕 Auto-transición: cierre → inscrito después de 7 días
  const AUTO_INSCRITO_DAYS = 7

  const autoPromoteCierreLeads = useCallback(async (leadsToCheck = []) => {
    if (!Array.isArray(leadsToCheck) || leadsToCheck.length === 0) return

    const leadsToPromote = leadsToCheck.filter((lead) => {
      const stage = normalizeLeadStage(lead.estado_lead || "")
      if (stage !== "cierre") return false
      const daysSinceUpdate = getDaysSinceUpdate(lead)
      return daysSinceUpdate > AUTO_INSCRITO_DAYS
    })

    if (!leadsToPromote.length) return

    const promotePromises = leadsToPromote.map((lead) => {
      const days = getDaysSinceUpdate(lead)
      return updateLead(lead.id, {
        estado_lead: "inscrito",
        fecha_inscripcion: lead.fecha_inscripcion || new Date().toISOString(),
        notas: `[Sistema] Promovido a Inscrito automáticamente tras ${days} días en Cierre de inscripción. ${lead.notas || ""}`.trim(),
      })
    })

    try {
      await Promise.all(promotePromises)
      // Auto-inscrito completado
      loadLeads({ showLoader: false })
    } catch (error) {
      console.error("❌ [Auto-Inscrito] Error promoviendo leads:", error)
    }
  }, [loadLeads])

  // Solo ejecutar auto-promoción una vez por carga (no en cada cambio de leads,
  // porque cada updateLead dispara el useEffect en bucle).
  const autoPromoteRanRef = useRef(false)
  useEffect(() => {
    if (leads.length && !autoPromoteRanRef.current) {
      autoPromoteRanRef.current = true
      autoPromoteCierreLeads(leads)
    }
  }, [autoPromoteCierreLeads, leads])

  useEffect(() => {
    if (userEmail || isAdmin) {
      loadLeads()
    }
  }, [isAdmin, userEmail, loadLeads])

  // Atajo de teclado: Ctrl+K / Cmd+K para buscar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
      if (e.key === "Escape" && searchQuery) {
        setSearchQuery("")
        searchInputRef.current?.blur()
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [searchQuery])

  // 🆕 Optimización: Suscripción a cambios en LEADS con actualización local
  useEffect(() => {
    if (!userEmail && !isAdmin) return

    // Channel name único por sesión: evita colisiones entre tabs/usuarios
    const channelName = `leads-changes-${userEmail || "anon"}-${Date.now()}`
    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "leads",
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload

          // Si es un INSERT, necesitamos cargar para obtener datos relacionados (joins)
          // O si es DELETE, eliminar localmente
          if (eventType === "DELETE") {
            setLeads((prev) => sortLeads(prev.filter((l) => l.id !== oldRecord.id)))
            return
          }

          if (eventType === "INSERT") {
            // Las CAPs también deben ver leads SIN asesor (bandeja común)
            // + sus asignados. Solo filtramos si el nuevo lead claramente
            // pertenece a OTRA asesora (asesor_id distinto al mío y no es null).
            if (
              !isAdmin &&
              asesorId &&
              newRecord.asesor_id &&
              newRecord.asesor_id !== asesorId
            ) {
              return
            }
            // Inserción optimista: el lead aparece arriba INMEDIATO sin esperar refetch.
            // Después loadLeads hidrata joins (asesor:asesor_id, campus:campus_id, etc.).
            setLeads((prev) => {
              if (prev.some((l) => l.id === newRecord.id)) return prev
              return sortLeads([newRecord, ...prev])
            })
            loadLeads({ showLoader: false })
            return
          }

          if (eventType === "UPDATE") {
            // Actualización optimista: fusionar cambios con el lead existente
            // Esto preserva campos populados (joins) que no vienen en el payload
            setLeads((prev) => {
              const idx = prev.findIndex(l => l.id === newRecord.id)
              if (idx === -1) {
                // Si no estaba en lista (ej. cambio de asignación o filtro), recargar por seguridad
                loadLeads({ showLoader: false })
                return prev
              }

              const existing = prev[idx]
              // Verificar si cambiaron campos críticos que requieren re-fetch
              if (!isAdmin && newRecord.asesor_id !== existing.asesor_id && newRecord.asesor_id !== asesorId) {
                // El lead se asignó a otro asesor
                return sortLeads(prev.filter(l => l.id !== newRecord.id))
              }

              const updated = { ...existing, ...newRecord }
              // Asegurar que updated_at sea objeto Date si lo necesitamos, o string. Normalmente Supabase devuelve string ISO.

              const newLeads = [...prev]
              newLeads[idx] = updated
              return sortLeads(newLeads)
            })
          }
        },
      )
      .subscribe((status, err) => {
        if (status === "SUBSCRIBED") {
          console.log(`[realtime] leads suscrito: ${channelName}`)
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.warn(`[realtime] leads ${status}:`, err)
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [asesorId, isAdmin, loadLeads, sortLeads, userEmail])

  // 🆕 Suscripción a MENSAJES en tiempo real (Global) - OPTIMIZADA
  // Incluye auto-restore: si un lead archivado responde → se restaura como "nuevo"
  useEffect(() => {
    if (!userEmail && !isAdmin) return

    const channel = supabase
      .channel(`kanban-messages-global-${userEmail || "anon"}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        async (payload) => {
          const newMessage = payload.new
          const leadId = newMessage.lead_id || newMessage.lead

          if (!leadId) return

          // 🆕 AUTO-RESTORE: Si el mensaje es del prospecto y el lead está
          // archivado por inactividad, restaurarlo al Kanban como "nuevo".
          //   - inactivo → SIEMPRE auto-restaura (7d sin respuesta, ahora volvió)
          //   - perdido  → solo si tiene fecha_archivado (archivado manualmente
          //                con la intención de poder reactivarse)
          //   - spam     → NUNCA auto-restaura (intención explícita de descartar;
          //                si quieren rescatarlo, lo hacen desde el modal de
          //                archivados manualmente)
          if (newMessage.direction === "in" || newMessage.actor === "cliente") {
            const currentLeads = leadsRef.current || []
            const archivedLead = currentLeads.find((l) => {
              if (l.id !== leadId) return false
              const stage = normalizeLeadStage(l.estado_lead || "")
              if (stage === "perdido") return Boolean(l.fecha_archivado)
              return stage === "inactivo"
            })

            if (archivedLead) {
              await updateLead(leadId, {
                estado_lead: "nuevo",
                fecha_archivado: null,
                recordatorio_1_at: null,
                recordatorio_2_at: null,
                recordatorio_3_at: null,
                ultima_interaccion: new Date().toISOString(),
              })

              const prevStage = normalizeLeadStage(archivedLead.estado_lead || "")
              const stageLabel =
                prevStage === "inactivo" ? "inactivo" :
                "archivado"

              toast.success("Lead restaurado", {
                description: `${archivedLead.nombre_tutor || 'Prospecto'} (estaba ${stageLabel}) respondió y volvió al Kanban.`,
              })

              loadLeads({ showLoader: false })
              return
            }
          }

          // 🆕 RED DE SEGURIDAD: si llega un mensaje de un lead que NO está en el
          // tablero, su INSERT de realtime se perdió/llegó fuera de orden bajo
          // ráfaga. El evento de mensaje sí llegó (por eso suena), así que
          // recargamos para que el prospecto aparezca solo, sin refrescar a mano.
          if (!(leadsRef.current || []).some(l => l.id === leadId)) {
            scheduleReload()
          } else {
            // Actualizar localmente el lead afectado sin recargar todo.
            // Tocamos ultima_interaccion + updated_at para que el sort (que usa
            // ese campo) suba el lead hasta arriba, sin importar si el mensaje
            // es IN (cliente) o OUT (CAP/bot) — cualquier actividad es señal de
            // conversación viva.
            setLeads((prev) => {
              const idx = prev.findIndex(l => l.id === leadId)
              if (idx === -1) return prev

              const existing = prev[idx]
              const ts = newMessage.created_at || new Date().toISOString()
              const updated = {
                ...existing,
                ultima_interaccion: ts,
                updated_at: ts,
              }

              const newLeads = [...prev]
              newLeads[idx] = updated
              return sortLeads(newLeads)
            })
          }

          // Dispatch event for lead-cards to update unread counts
          window.dispatchEvent(
            new CustomEvent("globalNewMessage", {
              detail: {
                leadId,
                direction: newMessage.direction,
                actor: newMessage.actor,
                createdAt: newMessage.created_at,
              },
            })
          )
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current)
    }
  }, [isAdmin, userEmail, sortLeads, scheduleReload])

  const handleDragStart = (lead) => {
    // [debug removed]
    setDraggedLead(lead)
  }

  const handleDragEnd = () => {
    // [debug removed]
    setDraggedLead(null)
  }

  const handleUnarchiveLead = async (lead) => {
    const { data, error } = await updateLead(lead.id, {
      estado_lead: toDbStatus("nuevo"),
      fecha_archivado: null,
      recordatorio_1_at: null,
      recordatorio_2_at: null,
      recordatorio_3_at: null,
    })
    if (!error && data) {
      setLeads((prev) => sortLeads(prev.map((item) => (item.id === lead.id ? { ...item, ...data } : item))))
      setLastSynced(new Date())
      toast.success("Prospecto restaurado al Kanban", {
        description: `${lead.nombre_tutor || "Prospecto"} regresó a "Primer Contacto".`,
      })
    } else if (error) {
      toast.error("No se pudo restaurar el prospecto", {
        description: error.message || "Inténtalo de nuevo.",
      })
    }
  }

  const handleDeleteLead = async (lead) => {
    // Usa la función deleteLead de @/lib/supabase que limpia:
    // - leads (con CASCADE: messages, lead_children, tasks, lead_activities, activities)
    // - n8n_chat_histories (memoria del bot por session_id)
    // - messages huérfanos (con lead_id null pero mismo wa_jid)
    const { error } = await deleteLead(lead.id)
    if (!error) {
      setLeads((prev) => prev.filter((item) => item.id !== lead.id))
      setLastSynced(new Date())
      toast.success("Prospecto eliminado completamente")
    } else {
      console.error("Error deleting lead:", error)
      toast.error("No se pudo eliminar el lead. Inténtalo de nuevo.")
    }
  }

  const handleDrop = async (columnId) => {
    // [debug removed]

    if (!draggedLead) {
      // [debug removed]
      return
    }

    const currentStage = normalizeLeadStage(draggedLead.estado_lead)
    // [debug removed]

    // Si el lead ya está en la columna destino, no hacer nada
    if (currentStage === columnId) {
      // [debug removed]
      setDraggedLead(null)
      return
    }

    // Validación SUAVE de transiciones: las CAPs conservan libertad total,
    // pero los saltos grandes (≥3 etapas en cualquier dirección) piden
    // confirmación para evitar drags accidentales (p.ej. nuevo → inscrito).
    {
      const fromKey = normalizeLeadStage(draggedLead.estado_lead)
      const toKey = normalizeLeadStage(toDbStatus(columnId))
      const fromOrder = STAGES[fromKey]?.kanbanOrder
      const toOrder = STAGES[toKey]?.kanbanOrder
      if (Number.isInteger(fromOrder) && Number.isInteger(toOrder) && Math.abs(toOrder - fromOrder) >= 3) {
        const fromLabel = LEAD_STAGE_META[fromKey]?.title || fromKey
        const toLabel = LEAD_STAGE_META[toKey]?.title || toKey
        const ok = window.confirm(
          `Vas a mover a ${draggedLead.nombre_tutor || "este lead"} de "${fromLabel}" a "${toLabel}" (${Math.abs(toOrder - fromOrder)} etapas de golpe).\n\n¿Confirmas el movimiento?`
        )
        if (!ok) { setDraggedLead(null); return }
      }
    }

    // Abrir modal según columna destino que requiere información adicional (y archivado)
    if (["solicita_cita", "aplicar_examen", "perdido"].includes(columnId)) {
      // [debug removed]
      setStatusModalData({ lead: draggedLead, targetStatus: columnId })
      setStatusModalOpen(true)
      return
    }

    // Para las demás columnas, actualizar directamente
    // [debug removed]
    const leadId = draggedLead.id
    const isCierre = columnId === "cierre_inscripcion" || columnId === "cierre"
    const updates = { estado_lead: toDbStatus(columnId) }

    if (isCierre) {
      updates.fecha_inscripcion = new Date().toISOString()
      updates.intervencion_humana = true
      // [debug removed]
    }

    const { data, error: updateError } = await updateLead(leadId, updates)

    if (updateError) {
      console.error("❌ [Drop] Error al actualizar:", updateError)
    } else if (data) {
      // [debug removed]
      setLeads((prev) => sortLeads(prev.map((lead) => (lead.id === leadId ? { ...lead, ...data } : lead))))
      setLastSynced(new Date())
    }

    setDraggedLead(null)
  }

  const handleArchiveLead = async () => {
    // [debug removed]

    if (!draggedLead) {
      // [debug removed]
      return
    }

    // [debug removed]
    setStatusModalData({ lead: draggedLead, targetStatus: "archivado" })
    setStatusModalOpen(true)
    setDraggedLead(null)
  }

  const handleMarkExamApplied = async (lead) => {
    if (!lead) return
    const confirmed = window.confirm(`¿Confirmas que ${lead.nombre_tutor || "el lead"} ya aplicó el examen?`)
    if (!confirmed) return

    const autoNote = appendLeadNote(
      lead,
      `${user?.nombre || "Asesor"} marcó el examen como aplicado manualmente.`
    )

    const { data, error } = await updateLead(lead.id, {
      estado_lead: toDbStatus("cierre_inscripcion"),
      aplico_examen: true,
      fecha_inscripcion: new Date().toISOString(),
      notas: autoNote,
      intervencion_humana: true,
    })

    if (error) {
      console.error("❌ [ExamComplete] Error al marcar examen aplicado:", error)
      toast.error("No pudimos actualizar el lead. Inténtalo nuevamente.")
      return
    }

    if (data) {
      setLeads((prev) => sortLeads(prev.map((item) => (item.id === lead.id ? { ...item, ...data } : item))))
      setLastSynced(new Date())
    }
  }

  const handleStatusConfirm = async (formData) => {
    const lead = statusModalData.lead
    const wasDetailOpen = detailLead?.id === lead?.id
    const targetStatus = statusModalData.targetStatus

    if (!lead) {
      // [debug removed]
      return
    }

    // [debug removed]

    const isCierre = targetStatus === "cierre_inscripcion"
    const updates = { estado_lead: toDbStatus(targetStatus) }

    // Solo activar intervencion_humana al llegar a cierre de inscripción
    if (isCierre) {
      updates.intervencion_humana = true
    }

    // Aplicar campos específicos según el tipo de transición
    if (isCierre) {
      if (formData.fecha) {
        updates.fecha_proximo_seguimiento = formData.fecha
      }
      const aprobado = formData.examenAprobado
      if (aprobado === false) {
        updates.estado_lead = toDbStatus("archivado")
        updates.aplico_examen = false
        const detail = formData.notas ? `Notas: ${formData.notas}` : ""
        const autoNote = `Examen reprobado. ${detail}`.trim()
        updates.notas = appendLeadNote(lead, autoNote)
      } else {
        updates.aplico_examen = true
        updates.fecha_inscripcion = new Date().toISOString()
        const detail = formData.notas ? formData.notas : "Lead aprobado en examen y listo para cierre."
        updates.notas = appendLeadNote(lead, detail)
      }
    } else if (targetStatus === "archivado") {
      // Si el lead ya está "inscrito", archivar = confirmar inscripción
      // (lo manda al histórico de inscritos en lugar de a perdidos)
      if (String(lead.estado_lead || "").toLowerCase() === "inscrito") {
        delete updates.estado_lead  // mantener "inscrito"
        updates.inscripcion_confirmada_at = new Date().toISOString()
        const detail = `${formData.razon || ""}${formData.notas ? ` - ${formData.notas}` : ""}`.trim()
        if (detail) {
          updates.notas = appendLeadNote(lead, `Inscripción confirmada al archivar. ${detail}`)
        } else {
          updates.notas = appendLeadNote(lead, `${user?.nombre || "Asesor"} confirmó la inscripción al archivar.`)
        }
      } else {
        const notasDescarte = `Razón: ${formData.razon || "Sin especificar"}${formData.notas ? `\nNotas: ${formData.notas}` : ""}`
        updates.notas = notasDescarte
      }
    } else if (targetStatus === "agendar_cita") {
      if (formData.fecha) {
        updates.fecha_proximo_seguimiento = formData.fecha
      }
      if (formData.notas) {
        updates.notas = appendLeadNote(lead, formData.notas)
      }
    } else if (targetStatus === "entrevista") {
      // La asesora confirma que el lead pasa a entrevista
      if (formData.fecha) {
        updates.fecha_proximo_seguimiento = formData.fecha
      }
      if (formData.notas) {
        updates.notas = appendLeadNote(lead, `Entrevista: ${formData.notas}`)
      } else {
        updates.notas = appendLeadNote(lead, `${user?.nombre || "Asesora"} activó la entrevista previa.`)
      }
    } else if (targetStatus === "aplicar_examen") {
      if (formData.fecha) {
        updates.fecha_proximo_seguimiento = formData.fecha
      }
      updates.aplico_examen = Boolean(formData.aplicoExamen)
      const updatedTags = ensureLeadTag(lead.tags, "examen")
      if (updatedTags.length > 0) {
        updates.tags = updatedTags
      }
      if (formData.notas) {
        updates.notas = appendLeadNote(lead, formData.notas)
      }
    } else {
      // Para otros casos, solo guardar notas si existen
      if (formData.notas) {
        updates.notas = appendLeadNote(lead, formData.notas)
      }
    }

    // [debug removed]

    const { data, error: updateError } = await updateLead(lead.id, updates)

    if (updateError) {
      console.error("❌ [StatusConfirm] Error al actualizar:", updateError)
      toast.error("No pudimos actualizar el lead. Por favor, intenta de nuevo.")
    } else if (data) {
      // [debug removed]
      setLeads((prev) => sortLeads(prev.map((l) => (l.id === lead.id ? { ...l, ...data } : l))))
      setLastSynced(new Date())
      if (wasDetailOpen && targetStatus === "archivado") setDetailLead(null)
    }

    setDraggedLead(null)
    setStatusModalOpen(false)
    setStatusModalData({ lead: null, targetStatus: null })
  }

  const handleStatusModalClose = () => {
    setStatusModalOpen(false)
    setStatusModalData({ lead: null, targetStatus: null })
    setDraggedLead(null)
  }

  const handleViewDetails = (lead) => setDetailLead(lead)

  const handleSaveLead = async (updatedLead) => {
    const updates = {
      nombre_tutor: updatedLead.nombre_tutor,
      telefono: updatedLead.telefono,
      email: updatedLead.email,
      nivel_educativo: updatedLead.nivel_educativo,
      estado: updatedLead.estado,
      municipio: updatedLead.municipio,
      preferencia_contacto: updatedLead.preferencia_contacto,
      prioridad: updatedLead.prioridad,
      calidad_lead: updatedLead.calidad_lead,
      notas: updatedLead.notas,
      nombre_alumno: updatedLead.nombre_alumno || null,
      grado_especifico: updatedLead.grado_especifico || null,
      es_familia: updatedLead.es_familia ?? false,
      num_hijos: updatedLead.num_hijos ?? 1,
      tema_consulta: updatedLead.tema_consulta || null,
      // Resetear inactividad: al guardar manualmente, el asesor atendió al lead
      ultima_interaccion: new Date().toISOString(),
    }
    if (updatedLead.asesor_email || updatedLead.asesor_nombre) updates.asesor_email = updatedLead.asesor_email || updatedLead.asesor_nombre
    if (updatedLead.campus != null) updates.campus = updatedLead.campus
    if (updatedLead.fecha_cita != null || updatedLead.fecha_proximo_seguimiento != null) {
      const citaValue = updatedLead.fecha_cita || updatedLead.fecha_proximo_seguimiento || null
      updates.fecha_proximo_seguimiento = citaValue
      updates.fecha_cita = citaValue
      // Auto-mover a "cita" cuando se asigna una fecha de cita
      if (citaValue) {
        const originalLead = leads.find((l) => l.id === updatedLead.id)
        const currentStage = normalizeLeadStage(originalLead?.estado_lead || updatedLead.estado_lead || "")
        const stagesBeforeCita = ["nuevo", "contactado", "cita_solicitada"]
        if (stagesBeforeCita.includes(currentStage)) {
          updates.estado_lead = "cita"
        }
      }
    }

    const autoMovedToCita = updates.estado_lead === "cita"

    const { data, error: updateError } = await updateLead(updatedLead.id, updates)

    if (!updateError && data) {
      setLeads((prev) => sortLeads(prev.map((l) => (l.id === data.id ? { ...l, ...data } : l))))
      setDetailLead((prev) => (prev?.id === data.id ? { ...prev, ...data } : prev))
      setLastSynced(new Date())
      if (autoMovedToCita) {
        toast.success("Cita agendada", { description: "El prospecto se movió a Citados automáticamente." })
      } else {
        toast.success("Guardado correctamente", { description: "Los cambios del prospecto se guardaron." })
      }
    } else {
      toast.error("Error al guardar", { description: "No pudimos guardar los cambios. Intenta de nuevo." })
    }
  }

  const handleRequestArchive = (lead) => {
    setStatusModalData({ lead, targetStatus: "archivado" })
    setStatusModalOpen(true)
  }

  // 🆕 Verificar si hay un lead que debemos resaltar (de las notificaciones)
  useEffect(() => {
    const highlightId = localStorage.getItem('highlight_lead')
    if (highlightId) {
      setHighlightedLead(highlightId)

      // Limpiar después de 5 segundos
      const timer = setTimeout(() => {
        setHighlightedLead(null)
        localStorage.removeItem('highlight_lead')
      }, 5000)

      return () => {
        clearTimeout(timer)
        localStorage.removeItem('highlight_lead')
      }
    }
  }, [])

  useEffect(() => {
    const handleHighlightLead = (event) => {
      const leadId = event?.detail?.leadId || null
      if (!leadId) return
      setHighlightedLead(leadId)
      setTimeout(() => {
        setHighlightedLead((current) => (current === leadId ? null : current))
      }, 5000)
      try {
        localStorage.removeItem('highlight_lead')
      } catch { }
    }

    window.addEventListener('highlightLead', handleHighlightLead)
    return () => window.removeEventListener('highlightLead', handleHighlightLead)
  }, [])

  // Scroll horizontal con rueda del mouse en el Kanban (para escritorio)
  useEffect(() => {
    const el = kanbanScrollRef.current
    if (!el) return
    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault()
        el.scrollLeft += e.deltaY
      }
    }
    el.addEventListener("wheel", handleWheel, { passive: false })
    return () => el.removeEventListener("wheel", handleWheel)
  }, [])

  const openConversationProcessedRef = useRef(false)
  useEffect(() => {
    if (!leads.length || openConversationProcessedRef.current) return
    const leadIdToOpen = localStorage.getItem('open_conversation_lead')
    if (!leadIdToOpen) return
    localStorage.removeItem('open_conversation_lead')
    openConversationProcessedRef.current = true
    const targetLead = leads.find((lead) => lead.id === leadIdToOpen)
    if (!targetLead) return
    setDetailLead(targetLead)
  }, [leads])

  useEffect(() => {
    const handleOpenLeadChat = async (event) => {
      const leadId = event?.detail?.leadId
      if (!leadId) return
      const currentLeads = leadsRef.current || []
      const targetLead = currentLeads.find((lead) => lead.id === leadId)
      if (targetLead) {
        setDetailLead(targetLead)
        return
      }
      // Lead no está en cache (puede ser de fuera del scope del usuario o aún no cargó):
      // lo fetcheamos directo de la DB y lo abrimos sin tocar el state de leads.
      try {
        const { data, error } = await supabase
          .from("v_leads_completos")
          .select("*")
          .eq("id", leadId)
          .maybeSingle()
        if (error || !data) {
          localStorage.setItem('open_conversation_lead', leadId)
          return
        }
        // Normalizar igual que getLeads para que el panel funcione
        const normalized = {
          ...data,
          campus: data.campus_nombre ?? data.campus ?? data.campus_id ?? null,
          campus_codigo: data.campus_codigo ?? null,
          campus_estado: data.campus_estado ?? null,
          campus_municipio: data.campus_municipio ?? null,
          asesor_email: data.asesor_email ?? null,
          asesor_nombre: data.asesor_nombre ?? null,
        }
        setDetailLead(normalized)
      } catch (err) {
        console.warn("[KanbanPage] handleOpenLeadChat fetch:", err?.message)
        localStorage.setItem('open_conversation_lead', leadId)
      }
    }

    window.addEventListener('openLeadChat', handleOpenLeadChat)
    return () => window.removeEventListener('openLeadChat', handleOpenLeadChat)
  }, [])

  // Al confirmar inscripción: marca el flag local AL INSTANTE (optimistic UI).
  // Si después la BD falla, escuchamos 'leadConfirmReverted' y revertimos.
  useEffect(() => {
    const handleLeadConfirmed = (e) => {
      const leadId = e?.detail?.leadId
      const data = e?.detail?.data
      if (!leadId) return
      setLeads((prev) =>
        prev.map((l) => {
          if (l.id !== leadId) return l
          const ts =
            data?.inscripcion_confirmada_at || new Date().toISOString()
          return { ...l, inscripcion_confirmada_at: ts }
        })
      )
    }
    const handleLeadConfirmReverted = (e) => {
      const leadId = e?.detail?.leadId
      if (!leadId) return
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, inscripcion_confirmada_at: null } : l,
        ),
      )
    }
    window.addEventListener('leadConfirmed', handleLeadConfirmed)
    window.addEventListener('leadConfirmReverted', handleLeadConfirmReverted)
    return () => {
      window.removeEventListener('leadConfirmed', handleLeadConfirmed)
      window.removeEventListener('leadConfirmReverted', handleLeadConfirmReverted)
    }
  }, [])

  // Listeners para atajos globales (Cmd/Ctrl+N nuevo prospecto, Cmd/Ctrl+T tareas)
  useEffect(() => {
    const handleNewLead = () => setCreateModalOpen(true)
    const handleTasksPanel = () => setTasksPanelOpen(true)
    const handleNotificationsCount = (e) => setNotificationsCount(Number(e?.detail?.count || 0))
    window.addEventListener('openNewLead', handleNewLead)
    window.addEventListener('openTasksPanel', handleTasksPanel)
    window.addEventListener('notificationsCountSync', handleNotificationsCount)
    return () => {
      window.removeEventListener('openNewLead', handleNewLead)
      window.removeEventListener('openTasksPanel', handleTasksPanel)
      window.removeEventListener('notificationsCountSync', handleNotificationsCount)
    }
  }, [])



  // Filtros globales: nivel + calidad (se aplican al tablero)
  // Restricción por asesora: Gaby solo Kinder/Pre-First, Lulú solo Primaria/Secundaria/Prepa
  // Maternal ya no se filtra desde aquí (se descontinuó como nivel ofrecido)
  const isGabyUser = userEmail.includes('gaby') || (user?.nombre || "").toLowerCase().includes('gaby')
  const isLuluUser = userEmail.includes('lulu') || (user?.nombre || "").toLowerCase().includes('lulu') || (user?.nombre || "").toLowerCase().includes('lulú')

  const baseNiveles = [
    { value: "kinder", label: "Kinder" },
    { value: "prefirst", label: "Pre-First" },
    { value: "primaria", label: "Primaria" },
    { value: "secundaria", label: "Secundaria" },
    { value: "preparatoria", label: "Prepa" },
  ]

  const NIVEL_FILTERS = isGabyUser
    ? baseNiveles.filter(n => ["kinder", "prefirst"].includes(n.value))
    : isLuluUser
      ? baseNiveles.filter(n => ["primaria", "secundaria", "preparatoria"].includes(n.value))
      : baseNiveles
  const CALIDAD_FILTERS = [
    { value: "caliente", label: "Caliente", icon: Flame, iconColor: "#ef4444" },
    { value: "tibio", label: "Tibio", icon: Zap, iconColor: "#f59e0b" },
    { value: "frio", label: "Frío", icon: Snowflake, iconColor: "#3b82f6" },
  ]

  const hasActiveFilters = Boolean(filterNivel || filterCalidad)

  // Leads filtrados globalmente (se usan para las columnas).
  // Excluye leads cuyo estado no tiene columna en el Kanban (inactivo, spam,
  // legacy) — esos son visibles solo en reportes/admin, no aquí.
  const validKanbanStages = useMemo(
    () => new Set(KANBAN_COLUMNS.map((c) => c.id)),
    [],
  )

  const filteredLeads = useMemo(() => {
    let result = leads.filter((l) => {
      if (l.es_operativo === true) return false
      const stage = normalizeLeadStage(l.estado_lead || "")
      if (!validKanbanStages.has(stage)) return false  // oculta inactivo/spam
      return true
    })
    if (filterNivel) {
      result = result.filter((l) => (l.nivel_educativo || "").toLowerCase().includes(filterNivel))
    }
    if (filterCalidad) {
      result = result.filter((l) => (l.calidad_lead || "").toLowerCase() === filterCalidad)
    }
    return result
  }, [leads, filterNivel, filterCalidad, validKanbanStages])

  // Búsqueda global de prospectos (dropdown)
  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q || q.length < 2) return []
    return leads.filter((lead) => {
      const name = (lead.nombre_tutor || "").toLowerCase()
      const phone = (lead.telefono || "").toLowerCase()
      const email = (lead.email || "").toLowerCase()
      const nivel = (lead.nivel_educativo || "").toLowerCase()
      const campus = (lead.campus || "").toLowerCase()
      return name.includes(q) || phone.includes(q) || email.includes(q) || nivel.includes(q) || campus.includes(q)
    }).slice(0, 8)
  }, [leads, searchQuery])

  // Set de leadIds con transferencia pendiente (incoming + outgoing)
  const transferLeadIds = useMemo(() => {
    const set = new Set()
    pendingTransfers.forEach((t) => t.lead?.id && set.add(t.lead.id))
    outgoingTransfers.forEach((t) => t.lead_id && set.add(t.lead_id))
    return set
  }, [pendingTransfers, outgoingTransfers])

  const enrichLead = useCallback(
    (lead) => (transferLeadIds.has(lead.id) ? { ...lead, _pendingTransfer: true } : lead),
    [transferLeadIds],
  )

  // Pasar el ID resaltado a los componentes de columna
  const getLeadsByColumn = (columnId) => {
    const columnLeads = filteredLeads
      .filter((lead) => normalizeLeadStage(lead.estado_lead) === columnId)
      // Ocultar inscritos ya confirmados (viven solo en InscritosModal)
      .filter((lead) => !(columnId === "inscrito" && lead.inscripcion_confirmada_at))
      .map(enrichLead)

    if (highlightedLead) {
      const highlightedLeadObj = filteredLeads.find(l => l.id === highlightedLead)
      if (
        highlightedLeadObj &&
        normalizeLeadStage(highlightedLeadObj.estado_lead) === columnId &&
        !(columnId === "inscrito" && highlightedLeadObj.inscripcion_confirmada_at)
      ) {
        const withoutHighlighted = columnLeads.filter(l => l.id !== highlightedLead)
        return [enrichLead(highlightedLeadObj), ...withoutHighlighted]
      }
    }

    return columnLeads
  }

  const columnStats = useMemo(() => {
    return KANBAN_COLUMNS.reduce((acc, column) => {
      acc[column.id] = filteredLeads.filter((lead) => {
        if (normalizeLeadStage(lead.estado_lead) !== column.id) return false
        if (column.id === "inscrito" && lead.inscripcion_confirmada_at) return false
        return true
      }).length
      return acc
    }, {})
  }, [filteredLeads])

  const totalLeads = filteredLeads.length
  // Total histórico de inscritos (para conversionRate)
  const inscritoTotal = filteredLeads.filter(l => normalizeLeadStage(l.estado_lead) === "inscrito").length
  // Solo del mes actual (para HeroBanner — métrica que importa hoy)
  const inscritoCount = useMemo(() => {
    const now = new Date()
    const yyyy = now.getFullYear()
    const mm = now.getMonth()
    return filteredLeads.filter((l) => {
      if (normalizeLeadStage(l.estado_lead) !== "inscrito") return false
      const ref = l.inscripcion_confirmada_at || l.fecha_inscripcion
      if (!ref) return false
      const d = new Date(ref)
      return d.getFullYear() === yyyy && d.getMonth() === mm
    }).length
  }, [filteredLeads])
  const cierreCount = columnStats["cierre"] || 0
  const processedLeads = cierreCount + inscritoTotal + (columnStats["perdido"] || 0)
  const conversionRate = totalLeads > 0 ? ((cierreCount + inscritoTotal) / totalLeads) * 100 : 0

  // Conteo de leads por nivel educativo (usa leads sin filtrar para mostrar totales reales)
  const nivelCounts = useMemo(() => {
    const counts = {}
    leads.forEach((l) => {
      const nivel = (l.nivel_educativo || "").toLowerCase()
      if (nivel) counts[nivel] = (counts[nivel] || 0) + 1
    })
    return counts
  }, [leads])

  // Métricas adicionales para el hero banner
  const citasHoy = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    return filteredLeads.filter(l => {
      if (!l.fecha_cita) return false
      const cita = new Date(l.fecha_cita)
      return cita >= today && cita < tomorrow
    }).length
  }, [filteredLeads])

  // Sin atender = lead activo sin CAP asignada + sin actividad >=3 dias.
  // Excluye estados finales/archivados y leads donde una CAP ya esta atendiendo
  // (intervencion_humana=true), porque esos no son "sin atender".
  const ESTADOS_NO_SIN_ATENDER = new Set([
    "inscrito", "perdido", "spam", "inactivo", "cierre", "entrega_resultado",
  ])
  const sinAtenderLeads = useMemo(() => {
    return filteredLeads.filter(l => {
      const stage = normalizeLeadStage(l.estado_lead)
      if (ESTADOS_NO_SIN_ATENDER.has(stage)) return false
      if (l.inscripcion_confirmada_at) return false
      if (l.intervencion_humana === true) return false
      const lastUpdate = getLastInteractionDate(l)
      const daysSince = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24)
      return daysSince >= 3
    })
  }, [filteredLeads])
  const sinAtender = sinAtenderLeads.length

  const columnSummaries = useMemo(() => {
    return KANBAN_COLUMNS.map((column) => {
      const count = columnStats[column.id] ?? 0
      const percentage = totalLeads > 0 ? (count / totalLeads) * 100 : 0
      return {
        ...column,
        count,
        percentage,
      }
    })
  }, [columnStats, totalLeads])

  const inscritoLeads = useMemo(
    () => filteredLeads.filter((lead) => {
      const stage = normalizeLeadStage(lead.estado_lead)
      return stage === "inscrito" || stage === "cierre"
    }),
    [filteredLeads]
  )

  if (loading && !refreshing) {
    return (
      <ProtectedRoute>
        {({ onLogout }) => (
          <Sidebar onLogout={onLogout}>
            <div className="relative min-h-full bg-gradient-to-br from-slate-50 via-white to-rose-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-rose-950/20 pb-safe">
              <KanbanPageSkeleton />
            </div>
          </Sidebar>
        )}
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      {({ onLogout, user: currentUser }) => (
        <Sidebar onLogout={onLogout}>
          <div className="relative min-h-full bg-gradient-to-br from-slate-50 via-white to-rose-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-rose-950/20 pb-safe z-0 min-w-0">

            {/* Badge flotante de transferencias pendientes (no afecta layout) */}
            {pendingTransfers.length > 0 && (
              <button
                onClick={() => setActiveTransfer(pendingTransfers[0])}
                className="fixed top-3 right-3 z-50 flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white text-xs font-bold shadow-lg shadow-rose-600/30 ring-2 ring-white/40 dark:ring-slate-900/60 transition-all hover:scale-105 active:scale-95"
                title="Tienes transferencias pendientes de revisar"
              >
                <span className="relative flex h-5 w-5 items-center justify-center rounded-full bg-white/25">
                  <Bell className="h-3 w-3" />
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 ring-1 ring-rose-600 animate-pulse" />
                </span>
                <span className="hidden sm:inline">
                  {pendingTransfers.length === 1
                    ? "1 transferencia"
                    : `${pendingTransfers.length} transferencias`}
                </span>
                <span className="sm:hidden">{pendingTransfers.length}</span>
              </button>
            )}

            <main className="container-fluid mx-auto space-y-3 px-4 py-2 pb-24 sm:px-6 sm:pb-6 lg:px-10 xl:px-16 max-w-[1680px]">

              <div className="hidden lg:block">
                <HeroBanner
                  stats={{ total: totalLeads, procesados: processedLeads, conversion: formatPercentage(conversionRate), citasHoy, sinAtender, inscritos: inscritoCount }}
                  onSinAtenderClick={() => setSinAtenderOpen(true)}
                />
              </div>

              {/* Botón Actualizar integrado en la barra de búsqueda más abajo */}

              {error && (
                <div className="rounded-2xl border border-red-200 dark:border-red-900/50 bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-950/50 dark:to-pink-950/50 p-4 shadow-lg shadow-red-500/10">
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500 text-white flex-shrink-0">
                      <ArchiveX className="h-4 w-4" />
                    </div>
                    <p className="text-sm font-medium text-red-900 dark:text-red-100 flex-1">{error}</p>
                  </div>
                </div>
              )}

              {/* Barra de búsqueda + Filtros */}
              <section className="relative space-y-2">
                <div className="flex items-center gap-2">
                  {/* Input de búsqueda - compacto */}
                  <div className="relative flex-1 sm:flex-none sm:w-72">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Search className="h-4 w-4 text-slate-400" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => setSearchFocused(true)}
                      onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                      placeholder="Buscar prospecto..."
                      className="w-full h-9 sm:h-9 pl-9 pr-16 rounded-lg sm:rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-500/40 focus:border-rose-400 transition-all"
                    />
                    {searchQuery ? (
                      <button
                        onClick={() => { setSearchQuery(""); searchInputRef.current?.focus() }}
                        className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <kbd className="hidden sm:flex absolute inset-y-0 right-2 my-auto h-5 items-center gap-0.5 px-1.5 rounded border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-[10px] font-mono text-slate-500 dark:text-slate-400 pointer-events-none">
                        ⌘K
                      </kbd>
                    )}

                    {/* Dropdown de resultados */}
                    {searchFocused && searchQuery.trim().length >= 2 && (
                      <div className="absolute top-full left-0 right-0 sm:w-96 mt-1.5 z-50 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                        {searchResults.length === 0 ? (
                          <div className="px-4 py-5 text-center">
                            <Search className="h-7 w-7 text-slate-300 dark:text-slate-600 mx-auto mb-1.5" />
                            <p className="text-sm text-slate-500 dark:text-slate-400">Sin resultados</p>
                          </div>
                        ) : (
                          <div className="max-h-[300px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                            {searchResults.map((lead) => {
                              const stage = normalizeLeadStage(lead.estado_lead)
                              const stageMeta = LEAD_STAGE_META[stage]
                              // Lead "archivado" → no aparece en el Kanban pero sí en el buscador.
                              // Mostramos un botón Restaurar inline para que la CAP lo recupere
                              // sin tener que abrir el modal de Archivados.
                              const isArchivedLike = stage === "inactivo" || stage === "perdido" || stage === "spam" || stage === "cierre"
                              const stageLabel = isArchivedLike
                                ? (stage === "inactivo" ? "Inactivo" : stage === "perdido" ? "Perdido" : stage === "spam" ? "Spam" : "Cierre")
                                : (stageMeta?.title || stage)
                              return (
                                <div
                                  key={lead.id}
                                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                                >
                                  <button
                                    type="button"
                                    onMouseDown={(e) => { e.preventDefault(); setDetailLead(lead); setSearchQuery(""); setSearchFocused(false) }}
                                    className="flex-1 flex items-center gap-3 text-left min-w-0"
                                  >
                                    <div
                                      className="flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                                      style={{ backgroundColor: stageMeta?.accent || "#6366f1" }}
                                    >
                                      {(lead.nombre_tutor || "?").charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">{lead.nombre_tutor || "Sin nombre"}</p>
                                      <div className="flex items-center gap-2">
                                        {lead.telefono && (
                                          <span className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                                            <Phone className="h-3 w-3" />
                                            {lead.telefono}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                  <span
                                    className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${isArchivedLike ? "text-slate-700 bg-slate-200 dark:text-slate-200 dark:bg-slate-700" : "text-white"}`}
                                    style={isArchivedLike ? undefined : { backgroundColor: stageMeta?.accent || "#6366f1" }}
                                  >
                                    {stageLabel}
                                  </span>
                                  {isArchivedLike && (
                                    <button
                                      type="button"
                                      onMouseDown={async (e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        await handleUnarchiveLead(lead)
                                        setSearchQuery("")
                                        setSearchFocused(false)
                                      }}
                                      title="Restaurar al Kanban como nuevo"
                                      className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] font-bold shadow-sm transition-colors active:scale-95"
                                    >
                                      <ArchiveRestore className="h-3 w-3" />
                                      Restaurar
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Mobile filter toggle */}
                  <button
                    onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                    className={`sm:hidden flex items-center gap-1.5 px-3 py-2 h-9 rounded-lg border text-xs font-semibold transition-all active:scale-95 ${hasActiveFilters
                      ? "bg-rose-50 dark:bg-rose-900/30 border-rose-300 dark:border-rose-600 text-rose-700 dark:text-rose-300"
                      : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                      }`}
                  >
                    <Filter className="h-3.5 w-3.5" />
                    <span>Filtros</span>
                    {hasActiveFilters && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
                  </button>

                  {/* Desktop: Separador vertical */}
                  <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700 flex-shrink-0" />

                  {/* Desktop filters (always visible) */}
                  <div className="hidden sm:flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-shrink-0 hidden lg:inline">Nivel:</span>
                    {NIVEL_FILTERS.map((f) => {
                      const count = nivelCounts[f.value] || 0
                      return (
                        <button
                          key={f.value}
                          onClick={() => setFilterNivel(filterNivel === f.value ? null : f.value)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${filterNivel === f.value
                            ? "bg-rose-100 dark:bg-rose-900/50 border-rose-300 dark:border-rose-600 text-rose-700 dark:text-rose-200 shadow-sm"
                            : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                            }`}
                        >
                          {f.label}
                          <span className={`text-[9px] font-bold px-1 py-0.5 rounded-md leading-none ${filterNivel === f.value
                            ? "bg-rose-200/70 dark:bg-rose-800/50"
                            : "bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500"
                            }`}>{count}</span>
                        </button>
                      )
                    })}

                    <div className="hidden sm:block w-px h-5 bg-slate-200 dark:bg-slate-700 flex-shrink-0 mx-0.5" />

                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-shrink-0 hidden lg:inline">Calidad:</span>
                    {CALIDAD_FILTERS.map((f) => {
                      const FIcon = f.icon
                      return (
                        <button
                          key={f.value}
                          onClick={() => setFilterCalidad(filterCalidad === f.value ? null : f.value)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all border ${filterCalidad === f.value
                            ? "bg-amber-100 dark:bg-amber-900/50 border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-200 shadow-sm"
                            : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                            }`}
                        >
                          <FIcon className="h-3 w-3" style={{ color: f.iconColor }} />
                          {f.label}
                        </button>
                      )
                    })}

                    {hasActiveFilters && (
                      <button
                        onClick={() => { setFilterNivel(null); setFilterCalidad(null) }}
                        className="px-2 py-1 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 border border-red-200 dark:border-red-800 transition-all"
                      >
                        <X className="h-3 w-3 inline mr-0.5" />
                        Limpiar
                      </button>
                    )}
                  </div>

                  {/* Botón Mis Tareas — con badge de contador */}
                  <button
                    className={`relative inline-flex items-center justify-center gap-1.5 px-3 py-1.5 h-9 rounded-lg text-xs font-medium border shadow-sm transition-all active:scale-95 flex-shrink-0 ${
                      pendingTasksCount > 0
                        ? "bg-amber-50 dark:bg-amber-950/30 border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-950/40"
                        : "bg-white dark:bg-slate-800 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                    }`}
                    onClick={() => setTasksPanelOpen(true)}
                    title={pendingTasksCount > 0 ? `${pendingTasksCount} tarea${pendingTasksCount !== 1 ? "s" : ""} pendiente${pendingTasksCount !== 1 ? "s" : ""}` : "Sin tareas pendientes"}
                  >
                    <ListTodo className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Tareas</span>
                    {pendingTasksCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-rose-600 text-white shadow-sm ring-1 ring-white dark:ring-slate-900">
                        {pendingTasksCount > 99 ? "99+" : pendingTasksCount}
                      </span>
                    )}
                  </button>

                  {/* Botón Notificaciones — abre el panel de alertas (al lado de Tareas) */}
                  <button
                    className={`relative inline-flex items-center justify-center gap-1.5 px-3 py-1.5 h-9 rounded-lg text-xs font-medium border shadow-sm transition-all active:scale-95 flex-shrink-0 ${
                      notificationsCount > 0
                        ? "bg-rose-50 dark:bg-rose-950/30 border-rose-400 dark:border-rose-600 text-rose-800 dark:text-rose-200 hover:bg-rose-100 dark:hover:bg-rose-950/40"
                        : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                    }`}
                    onClick={() => window.dispatchEvent(new CustomEvent('openNotificationsPanel'))}
                    title={notificationsCount > 0 ? `${notificationsCount} notificación${notificationsCount !== 1 ? "es" : ""} sin leer` : "Notificaciones"}
                  >
                    <Bell className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Notificaciones</span>
                    {notificationsCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-rose-600 text-white shadow-sm ring-1 ring-white dark:ring-slate-900">
                        {notificationsCount > 99 ? "99+" : notificationsCount}
                      </span>
                    )}
                  </button>

                  {/* Botón Nuevo Prospecto */}
                  <button
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium shadow-sm transition-all active:scale-95 flex-shrink-0"
                    onClick={() => setCreateModalOpen(true)}
                    title="Registrar prospecto manualmente"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Nuevo Prospecto</span>
                  </button>
                </div>

                {/* Mobile filters panel (collapsible) */}
                {mobileFiltersOpen && (
                  <div className="sm:hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Nivel educativo</p>
                      <div className="flex flex-wrap gap-1.5">
                        {NIVEL_FILTERS.map((f) => {
                          const count = nivelCounts[f.value] || 0
                          return (
                            <button
                              key={f.value}
                              onClick={() => setFilterNivel(filterNivel === f.value ? null : f.value)}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border active:scale-95 ${filterNivel === f.value
                                ? "bg-rose-100 dark:bg-rose-900/50 border-rose-300 dark:border-rose-600 text-rose-700 dark:text-rose-200 shadow-sm"
                                : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                                }`}
                            >
                              {f.label}
                              <span className={`text-[9px] font-bold px-1 py-0.5 rounded-md leading-none ${filterNivel === f.value
                                ? "bg-rose-200/70 dark:bg-rose-800/50"
                                : "bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500"
                                }`}>{count}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Calidad del lead</p>
                      <div className="flex flex-wrap gap-1.5">
                        {CALIDAD_FILTERS.map((f) => {
                          const FIcon = f.icon
                          return (
                            <button
                              key={f.value}
                              onClick={() => setFilterCalidad(filterCalidad === f.value ? null : f.value)}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border active:scale-95 ${filterCalidad === f.value
                                ? "bg-amber-100 dark:bg-amber-900/50 border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-200 shadow-sm"
                                : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                                }`}
                            >
                              <FIcon className="h-3 w-3" style={{ color: f.iconColor }} />
                              {f.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {hasActiveFilters && (
                      <button
                        onClick={() => { setFilterNivel(null); setFilterCalidad(null); setMobileFiltersOpen(false) }}
                        className="w-full py-2 rounded-lg text-xs font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 transition-all active:scale-[0.98]"
                      >
                        <X className="h-3 w-3 inline mr-1" />
                        Limpiar filtros
                      </button>
                    )}
                  </div>
                )}
              </section>

              {/* Sección Kanban mejorada */}
              <section className="space-y-4 sm:space-y-5">
                {/* Header: Título a la izquierda, botones + flechas a la derecha */}
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Tablero Kanban</h2>
                    <p className="hidden sm:block text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                      Arrastra las tarjetas para actualizar el estado de tus prospectos y organizar tu proceso de captación.
                    </p>
                  </div>

                  {/* Botones + flechas alineados a la derecha */}
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <button
                      onClick={() => setInscritosOpen(true)}
                      className="inline-flex items-center gap-2 rounded-xl border border-teal-200 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold shadow-sm transition-colors text-teal-700 bg-white hover:bg-teal-50 dark:border-teal-800 dark:text-teal-200 dark:bg-slate-900/40 dark:hover:bg-slate-900/60 active:scale-95"
                    >
                      <UserCheck className="h-4 w-4" />
                      Inscritos ({inscritoLeads?.length || 0})
                    </button>
                    <button
                      onClick={() => setArchivedOpen(true)}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800 active:scale-95"
                    >
                      <ArchiveRestore className="h-4 w-4 text-slate-500 dark:text-slate-300" />
                      <span className="hidden xs:inline">Ver </span>Archivados
                    </button>

                    {/* Separador + flechas — solo desktop */}
                    <div className="hidden md:flex items-center ml-1 pl-3 border-l border-slate-200 dark:border-slate-700">
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => { const el = kanbanScrollRef.current; if (el) el.scrollBy({ left: -320, behavior: "smooth" }) }}
                            className="h-9 w-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-all shadow-sm active:scale-90"
                            title="Scroll izquierda"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => { const el = kanbanScrollRef.current; if (el) el.scrollBy({ left: 320, behavior: "smooth" }) }}
                            className="h-9 w-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-all shadow-sm active:scale-90"
                            title="Scroll derecha"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </div>
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 whitespace-nowrap">Desliza para ver más</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Columnas Kanban: Desktop = scroll horizontal, Mobile = tab picker */}

                {/* Desktop columns — rueda del mouse scrollea horizontalmente */}
                <div className="hidden md:block relative">
                <div className="pointer-events-none absolute right-0 top-9 bottom-4 w-10 bg-gradient-to-l from-slate-50 dark:from-slate-950 to-transparent z-10 opacity-50" />
                <motion.div
                  layout
                  className="flex gap-3 overflow-x-auto overflow-y-visible pb-4 scrollbar-styled"
                  style={{ minHeight: "min(720px, calc(100vh - 160px))" }}
                  ref={kanbanScrollRef}
                >
                  {KANBAN_COLUMNS.map((column) => (
                    <motion.div
                      layout
                      key={column.id}
                      className="flex-shrink-0 w-[clamp(240px,22vw,320px)] min-w-[240px]"
                    >
                      <KanbanColumn
                        column={column}
                        leads={getLeadsByColumn(column.id)}
                        onViewDetails={handleViewDetails}
                        onOpenConversation={(lead) => setDetailLead(lead)}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDrop={handleDrop}
                        onArchive={handleArchiveLead}
                        isDragging={draggedLead !== null}
                        statsCount={columnStats[column.id] ?? 0}
                        totalLeads={totalLeads}
                        highlightedLeadId={highlightedLead}
                      />
                    </motion.div>
                  ))}
                </motion.div>
                </div>

                {/* Mobile: Column pill picker + single column */}
                <div className="md:hidden space-y-3">
                  {/* Column picker pills */}
                  <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4">
                    {KANBAN_COLUMNS.map((column) => {
                      const count = columnStats[column.id] ?? 0
                      const ColIcon = column.icon
                      const isActive = mobileActiveColumn === column.id
                      return (
                        <button
                          key={column.id}
                          onClick={() => setMobileActiveColumn(column.id)}
                          className={`flex-shrink-0 flex items-center gap-2 rounded-2xl px-3.5 py-2.5 transition-all active:scale-95 ${isActive
                            ? "text-white shadow-lg"
                            : "bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300"
                            }`}
                          style={isActive ? { background: `linear-gradient(135deg, ${column.accent}DD, ${column.accent})` } : {}}
                        >
                          <ColIcon className={`h-4 w-4 ${isActive ? "text-white" : "text-slate-400"}`} />
                          <span className={`text-xs font-bold whitespace-nowrap ${isActive ? "text-white" : ""}`}>{column.title}</span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive
                            ? "bg-white/25 text-white"
                            : "bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
                            }`}>
                            {count}
                          </span>
                        </button>
                      )
                    })}
                  </div>

                  {/* Active column content */}
                  {(() => {
                    const activeCol = KANBAN_COLUMNS.find(c => c.id === mobileActiveColumn)
                    if (!activeCol) return null
                    return (
                      <KanbanColumn
                        column={activeCol}
                        leads={getLeadsByColumn(activeCol.id)}
                        onViewDetails={handleViewDetails}
                        onOpenConversation={(lead) => setDetailLead(lead)}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDrop={handleDrop}
                        onArchive={handleArchiveLead}
                        isDragging={draggedLead !== null}
                        statsCount={columnStats[activeCol.id] ?? 0}
                        totalLeads={totalLeads}
                        highlightedLeadId={highlightedLead}
                      />
                    )
                  })()}
                </div>
              </section>
            </main>


            <LeadUnifiedView
              key={detailLead?.id || "none"}
              lead={detailLead}
              user={user}
              open={Boolean(detailLead)}
              onClose={() => setDetailLead(null)}
              onSave={handleSaveLead}
              onRequestArchive={handleRequestArchive}
              onDelete={handleDeleteLead}
            />

            <LeadStatusModal
              open={statusModalOpen}
              onClose={handleStatusModalClose}
              lead={statusModalData.lead}
              targetStatus={statusModalData.targetStatus}
              onConfirm={handleStatusConfirm}
            />

            <ArchivedLeadsModal
              open={archivedOpen}
              onClose={() => setArchivedOpen(false)}
              leads={leads.filter((l) => {
                // Estados que NO aparecen en el Kanban pero SÍ deben ser
                // accesibles desde el modal de archivados:
                //   perdido  → manual ("ya no le interesa")
                //   inactivo → auto-archivo a 7 días sin respuesta
                //   spam     → marcado como spam
                //   cierre   → legacy (estado viejo sin columna)
                const stage = normalizeLeadStage(l.estado_lead || "")
                return stage === "perdido" || stage === "inactivo" || stage === "spam" || stage === "cierre"
              })}
              onRestore={handleUnarchiveLead}
              onDelete={handleDeleteLead}
              onViewDetails={(lead) => setDetailLead(lead)}
            />

            <InscritosModal
              open={inscritosOpen}
              onClose={() => setInscritosOpen(false)}
              leads={inscritoLeads}
              onViewDetails={(lead) => setDetailLead(lead)}
            />

            <SinAtenderModal
              open={sinAtenderOpen}
              onClose={() => setSinAtenderOpen(false)}
              leads={sinAtenderLeads}
              onViewDetails={(lead) => setDetailLead(lead)}
            />

            <LeadEditorModal
              mode="create"
              open={createModalOpen}
              onClose={() => setCreateModalOpen(false)}
              onSave={async (formData) => {
                const campusId = getCurrentCampus()
                // Separar hijos del payload del lead
                const { hijos = [], ...leadPayload } = formData
                const validHijos = (hijos || []).filter((h) => h?.nombre?.trim())

                // Si hay al menos un hijo válido, usar sus datos como "alumno principal"
                // (después el trigger de la DB también lo sincronizará al insertar lead_children).
                if (validHijos.length > 0) {
                  const first = validHijos[0]
                  if (!leadPayload.nombre_alumno) leadPayload.nombre_alumno = first.nombre
                  if (!leadPayload.nivel_educativo && first.nivel_educativo) leadPayload.nivel_educativo = first.nivel_educativo
                  if (!leadPayload.grado_especifico && first.grado_especifico) leadPayload.grado_especifico = first.grado_especifico
                  leadPayload.num_hijos = validHijos.length
                  leadPayload.es_familia = validHijos.length >= 2
                }

                if (campusId) leadPayload.campus_id = campusId

                const { data: newLead, error } = await createLead(leadPayload)
                if (error) {
                  toast.error("Error al crear prospecto: " + error.message)
                  return
                }
                if (!newLead) {
                  setCreateModalOpen(false)
                  return
                }

                // Insertar los hijos en lead_children
                if (validHijos.length > 0) {
                  let inserted = 0
                  for (let i = 0; i < validHijos.length; i++) {
                    const h = validHijos[i]
                    const { error: childErr } = await createLeadChild(newLead.id, {
                      nombre: h.nombre.trim(),
                      nivel_educativo: h.nivel_educativo || null,
                      grado_especifico: h.grado_especifico || null,
                      edad: h.edad || null,
                      sexo: h.sexo || null,
                      orden: i,
                    })
                    if (!childErr) inserted++
                  }
                  if (inserted < validHijos.length) {
                    toast.warning(`Prospecto creado, pero ${validHijos.length - inserted} hijo(s) fallaron al guardarse`)
                  }
                }

                setLeads((prev) => [newLead, ...prev])
                // Limpiar filtros para garantizar que el prospecto recién creado
                // sea visible en la columna "Primer Contacto" sin importar si
                // había un filtro activo de nivel o calidad.
                setFilterNivel(null)
                setFilterCalidad(null)
                toast.success(
                  validHijos.length >= 2
                    ? `Familia "${newLead.nombre_tutor}" creada con ${validHijos.length} hijos`
                    : `Prospecto "${newLead.nombre_tutor}" creado exitosamente`,
                )
                setCreateModalOpen(false)
              }}
            />

            <TasksPanel
              open={tasksPanelOpen}
              onClose={() => setTasksPanelOpen(false)}
              user={user}
              asesorId={asesorId}
              onNavigateToLead={(taskLead) => {
                const full = leads.find((l) => l.id === taskLead?.id) || taskLead
                if (full) {
                  setDetailLead(full)
                  setTasksPanelOpen(false)
                }
              }}
            />

            <TransferRequestModal
              open={!!activeTransfer}
              transfer={activeTransfer}
              currentUser={{ id: asesorId, ...user }}
              onClose={() => setActiveTransfer(null)}
              onResolved={(status) => {
                refreshTransfers()
                if (status === "accepted") {
                  setTimeout(() => loadLeads({ showLoader: false }), 300)
                }
              }}
            />
          </div>
        </Sidebar>
      )}
    </ProtectedRoute>
  )
}
